import { useCallback, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type { ViewToken } from 'react-native';
import { Typography, Flex, RemoteImage, IconCouponFilled, List, Colors } from '@banhala/ui-kit';
import type { MarketBadgeListProps, MarketBadgeItem } from './types';
import type { AnyObject } from '../../../../types';
import { bridge, isNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';

const PADDING_HORIZONTAL = 16;
const GAP = 14;
const BADGE_ITEM_WIDTH = 70;

type BadgeEntity = { item: MarketBadgeItem; logging: AnyObject };

interface MarketBadgeShortIconProps {
  item: MarketBadgeItem;
  logging: AnyObject;
}

const MarketBadgeShortIcon = ({ item, logging }: MarketBadgeShortIconProps) => {
  const { name, badge, deeplink, coupon_discount } = item;
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_STORE',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, deeplink, logging]);

  return (
    <Flex width={BADGE_ITEM_WIDTH} alignItems="center" gap={4} onPress={handlePress}>
      <Flex
        width={64}
        height={64}
        borderRadius={32}
        borderWidth={1}
        borderColor="gray20"
        overflow="hidden"
      >
        <RemoteImage uri={badge} width={64} height={64} />
      </Flex>

      <Flex width="100%">
        <Typography
          type="body2"
          color="content_primary"
          numberOfLines={1}
          textAlign="center"
        >
          {name}
        </Typography>
      </Flex>

      {coupon_discount && (
        <Flex.Row
          alignItems="center"
          justifyContent="center"
          backgroundColor="blue5"
          borderRadius={2}
          padding={{ horizontal: 6, vertical: 1 }}
          gap={2}
        >
          <IconCouponFilled width={12} height={12} fill={Colors.content_blue_primary} />
          <Typography type="body5" color="content_blue_primary">
            {coupon_discount}
          </Typography>
        </Flex.Row>
      )}
    </Flex>
  );
};

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 1 };

const keyExtractor = (item: BadgeEntity) => item.item.sno.toString();

const renderItem = ({ item }: { item: BadgeEntity }) => (
  <MarketBadgeShortIcon item={item.item} logging={item.logging} />
);

export const MarketBadgeList = ({ entity }: MarketBadgeListProps) => {
  const displayedRef = useRef(new Set<number>());

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        const badgeData = item as BadgeEntity;
        bridge.logAnalyticsEvent({
          name: 'DISPLAY_STORE',
          analytics: badgeData.logging.analytics,
          ably_only_log: badgeData.logging.ably_only_log,
        });
      });
    },
    [],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: PADDING_HORIZONTAL,
    gap: GAP,
    paddingBottom: 24,
  },
});
